/**
 * Angiolini Academy — Final Exam UI (5 parts).
 *
 * Flow:
 *   1. Trainee lands on /academy/final-exam.
 *   2. GET /eligibility — if not eligible, redirect back to /academy.
 *   3. Trainee starts a new attempt (or resumes an existing in-progress one).
 *   4. Each of 5 tabs (Product, Sales, CRM, Objections, Full Sale) can be
 *      submitted independently; server calculates score per part.
 *   5. Once all 5 have a score, "Завершить экзамен" locks the aggregate.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle2, Circle, Sparkles, Trophy } from "lucide-react";
import SimulatorChat from "./academy/SimulatorChat";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PARTS = [
  { key: "product",   label: "1. Продукт",     kind: "quiz",      part: 1 },
  { key: "sales",     label: "2. Продажи",     kind: "quiz",      part: 2 },
  { key: "crm",       label: "3. CRM",         kind: "practical", part: 3 },
  { key: "objections",label: "4. Возражения",  kind: "sim3",      part: 4 },
  { key: "full_sale", label: "5. Полная продажа", kind: "sim1",   part: 5 },
];

export default function AcademyFinalExam() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(null);
  const [activeTab, setActiveTab] = useState("product");
  const [finishing, setFinishing] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  const bootstrapped = useRef(false);

  useEffect(() => {
    // Guard against double-invocation (StrictMode / remount): a second
    // POST /start used to create a phantom attempt and lock the trainee
    // out for 24 h.
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    (async () => {
      try {
        const elig = await axios.get(`${API}/academy/final-exam/eligibility`,
          { withCredentials: true });
        const last = elig.data.last_attempt;
        const resumeId = elig.data.resumable_attempt_id;
        if (!elig.data.eligible && !resumeId) {
          if (elig.data.cooldown_active && elig.data.next_allowed_at) {
            const when = new Date(elig.data.next_allowed_at)
              .toLocaleString("ru-RU", { day: "2-digit", month: "2-digit",
                                          hour: "2-digit", minute: "2-digit" });
            alert(`Следующая попытка экзамена будет доступна ${when}. ` +
                   `Если это ошибка — попросите администратора снять блок ` +
                   `в разделе «Сотрудники».`);
          } else {
            alert(`Экзамен откроется после прохождения всех уроков ` +
                   `(${elig.data.lessons_completed}/${elig.data.lessons_total}).`);
          }
          navigate("/academy");
          return;
        }
        // Resume the live attempt if there is one, otherwise start a fresh one.
        const resume = resumeId || (last && last.status === "in_progress" ? last.id : null);
        if (resume) {
          const cur = await axios.get(`${API}/academy/final-exam/${resume}`,
            { withCredentials: true });
          setAttempt(cur.data);
        } else {
          const r = await axios.post(`${API}/academy/final-exam/start`, {},
            { withCredentials: true });
          setAttempt(r.data);
        }
      } catch (e) {
        alert(e.response?.data?.detail || "Не удалось загрузить экзамен");
        navigate("/academy");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    const r = await axios.get(`${API}/academy/final-exam/${attempt.id}`,
      { withCredentials: true });
    setAttempt(r.data);
  };

  const finish = async () => {
    setFinishing(true);
    try {
      const r = await axios.post(`${API}/academy/final-exam/${attempt.id}/finish`,
        {}, { withCredentials: true });
      setFinalResult(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Не удалось завершить экзамен");
    } finally { setFinishing(false); }
  };

  if (loading) return <div style={{ padding: 60, textAlign: "center" }}>
    <Loader2 size={26} className="animate-spin" />
  </div>;
  if (!attempt) return null;

  const scores = attempt.part_scores || {};
  const allDone = PARTS.every(p => scores[p.key] !== null && scores[p.key] !== undefined);
  const timeExpired = attempt.status === "in_progress" && !attempt.time_left_seconds;
  const canFinish = allDone || timeExpired;

  return (
    <div style={{ padding: "20px 24px", maxWidth: 900, margin: "0 auto",
                   color: "#0A0A0A", fontFamily: "Manrope" }}>
      <button data-testid="final-exam-back" onClick={() => navigate("/academy")}
        style={{ background: "transparent", border: "none", cursor: "pointer",
                  color: "#64748b", display: "inline-flex", alignItems: "center",
                  gap: 6, marginBottom: 14, fontSize: 13 }}>
        <ArrowLeft size={14} /> К академии
      </button>

      {finalResult ? (
        <FinalResult result={finalResult} scores={scores} navigate={navigate} />
      ) : (
        <>
          <Header attempt={attempt} scores={scores} onExpire={finish} />
          <TabsBar activeTab={activeTab} onSelect={setActiveTab} scores={scores} />
          <div style={{ marginTop: 16 }}>
            {PARTS.filter(p => p.key === activeTab).map(p => (
              <div key={p.key}>
                {p.kind === "quiz" && (
                  <QuizPart attempt={attempt} part={p} onDone={refresh} />
                )}
                {p.kind === "practical" && (
                  <PracticalPart attempt={attempt} onDone={refresh} />
                )}
                {p.kind === "sim3" && (
                  <ObjectionsPart attempt={attempt} onDone={refresh} />
                )}
                {p.kind === "sim1" && (
                  <FullSalePart attempt={attempt} onDone={refresh} />
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24, textAlign: "right" }}>
            <button data-testid="final-exam-finish"
              disabled={!canFinish || finishing}
              onClick={finish}
              style={{ background: canFinish ? "linear-gradient(135deg,#C5A059,#D4B878)"
                                            : "#e5e7eb",
                        color: canFinish ? "#0A0A0A" : "#94a3b8",
                        border: "none", borderRadius: 8, padding: "14px 28px",
                        fontWeight: 800, fontSize: 13, letterSpacing: 0.5,
                        textTransform: "uppercase",
                        cursor: canFinish && !finishing ? "pointer" : "not-allowed" }}>
              {finishing ? "Подсчёт…" :
                canFinish ? "Завершить экзамен" : "Пройдите все 5 частей"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}


function Header({ scores, attempt, onExpire }) {
  const doneCount = PARTS.filter(p => scores[p.key] !== null && scores[p.key] !== undefined).length;
  const [secondsLeft, setSecondsLeft] = useState(attempt.time_left_seconds || 0);

  useEffect(() => {
    if (attempt.status !== "in_progress") return undefined;
    // Never auto-finish on mount: if the server reports no time left we show
    // «Время вышло» and let the trainee close the attempt manually.
    if (!attempt.time_left_seconds) { setSecondsLeft(0); return undefined; }
    setSecondsLeft(attempt.time_left_seconds);
    const iv = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(iv);
          onExpire?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt.id, attempt.status, attempt.time_left_seconds]);

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");
  const isLow = secondsLeft > 0 && secondsLeft < 300;   // <5 min → red
  const isCritical = secondsLeft === 0;

  return (
    <div style={{ background: "linear-gradient(135deg,#0A0A0A,#1a1a1a)",
                   border: "1px solid #C5A059", borderRadius: 12,
                   padding: 22, marginBottom: 18, color: "#e5e7eb",
                   display: "flex", alignItems: "center", justifyContent: "space-between",
                   flexWrap: "wrap", gap: 12 }}>
      <div>
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 28, color: "#D4B878" }}>
          Финальный экзамен
        </div>
        <div style={{ fontSize: 12, color: "#a1a1aa", marginTop: 4 }}>
          Пройдено частей: {doneCount}/5 · Проходной балл — 80%
        </div>
      </div>
      {attempt.status === "in_progress" && (
        <div data-testid="final-exam-timer"
          style={{ background: isCritical ? "#7f1d1d"
                                            : isLow ? "#b91c1c" : "rgba(197,160,89,0.15)",
                    border: `1px solid ${isCritical || isLow ? "#dc2626" : "#C5A059"}`,
                    borderRadius: 10, padding: "10px 18px", textAlign: "center" }}>
          <div style={{ fontSize: 10, color: isLow ? "#fecaca" : "#a1a1aa",
                         textTransform: "uppercase", letterSpacing: 1 }}>
            {isCritical ? "Время вышло" : "Осталось"}
          </div>
          <div style={{ fontFamily: "monospace", fontSize: 26, fontWeight: 800,
                         color: isCritical || isLow ? "#fee2e2" : "#D4B878",
                         marginTop: 2 }}>
            {mm}:{ss}
          </div>
        </div>
      )}
    </div>
  );
}


function TabsBar({ activeTab, onSelect, scores }) {
  return (
    <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
      {PARTS.map(p => {
        const done = scores[p.key] !== null && scores[p.key] !== undefined;
        const isActive = activeTab === p.key;
        return (
          <button key={p.key} data-testid={`final-tab-${p.key}`}
            onClick={() => onSelect(p.key)}
            style={{ background: isActive ? "#0A0A0A" : "white",
                      color: isActive ? "#D4B878" : "#0A0A0A",
                      border: isActive ? "1px solid #C5A059" : "1px solid #e5e7eb",
                      borderRadius: 8, padding: "10px 14px", cursor: "pointer",
                      fontWeight: 700, fontSize: 12, whiteSpace: "nowrap",
                      display: "inline-flex", alignItems: "center", gap: 6 }}>
            {done ? <CheckCircle2 size={13} color="#22c55e" /> : <Circle size={13} color="#94a3b8" />}
            {p.label}
            {done && <span style={{ fontSize: 11, marginLeft: 4,
                                     color: isActive ? "#D4B878" : "#059669" }}>
              {scores[p.key]}%
            </span>}
          </button>
        );
      })}
    </div>
  );
}


function QuizPart({ attempt, part, onDone }) {
  const questions = part.part === 1
    ? attempt.product_questions
    : attempt.sales_questions;
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const unanswered = questions.filter(q => answers[q.question_uid] === undefined);
    if (unanswered.length > 0) {
      alert(`Ответьте на все вопросы. Пропущено: ${unanswered.length}.`);
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/academy/final-exam/${attempt.id}/submit-quiz`,
        { part: part.part, answers },
        { withCredentials: true });
      setResult(r.data);
      await onDone();
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка");
    } finally { setSubmitting(false); }
  };

  const doneScore = attempt.part_scores?.[part.key];
  if (doneScore !== null && doneScore !== undefined && !result) {
    return (
      <div data-testid={`final-part-${part.key}-done`}
        style={{ background: "white", border: "1px solid #d1fae5",
                  borderRadius: 10, padding: 24, textAlign: "center" }}>
        <Trophy size={32} color={doneScore >= 80 ? "#22c55e" : "#f97316"}
          style={{ margin: "0 auto 8px", display: "block" }} />
        <div style={{ fontSize: 32, fontWeight: 800,
                       color: doneScore >= 80 ? "#22c55e" : "#f97316" }}>
          {doneScore}%
        </div>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          Часть «{part.label.substring(3)}» сдана
        </div>
      </div>
    );
  }
  if (result) {
    return (
      <div style={{ background: "white", border: "1px solid #d1fae5",
                     borderRadius: 10, padding: 24 }}>
        <div style={{ fontSize: 24, fontWeight: 800, textAlign: "center",
                       color: result.score >= 80 ? "#22c55e" : "#f97316" }}>
          Результат: {result.score}% ({result.correct}/{result.total})
        </div>
      </div>
    );
  }
  return (
    <div>
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 12 }}>
        {questions.length} вопросов. У Вас одна попытка на эту часть.
      </div>
      {questions.map((q, i) => (
        <QuestionCard key={q.question_uid} q={q} index={i}
          picked={answers[q.question_uid]}
          onPick={(v) => setAnswers({ ...answers, [q.question_uid]: v })} />
      ))}
      <button data-testid={`final-quiz-submit-${part.key}`}
        onClick={submit} disabled={submitting}
        style={{ background: "#0A0A0A", color: "#D4B878", border: "none",
                  borderRadius: 8, padding: "12px 24px", cursor: "pointer",
                  fontWeight: 700, fontSize: 13, marginTop: 12 }}>
        {submitting ? "Отправка…" : "Сдать часть"}
      </button>
    </div>
  );
}


function QuestionCard({ q, index, picked, onPick }) {
  return (
    <div data-testid={`final-q-${q.question_uid}`}
      style={{ background: "white", border: "1px solid #efeee9",
                borderRadius: 8, padding: 16, marginBottom: 12 }}>
      {q.image && (
        <img src={q.image.startsWith("http") ? q.image : (process.env.REACT_APP_BACKEND_URL + q.image)}
          alt="" style={{ maxWidth: "100%", maxHeight: 300,
                          borderRadius: 6, marginBottom: 12, display: "block",
                          objectFit: "contain", background: "#faf9f5" }} />
      )}
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10,
                     color: "#0A0A0A" }}>
        {index + 1}. {q.question}
      </div>
      {q.options.map((opt, oi) => (
        <label key={oi} style={{ display: "flex", alignItems: "center", gap: 8,
                                   padding: "6px 0", cursor: "pointer",
                                   fontSize: 13, color: "#1f2937" }}>
          <input type="radio" name={q.question_uid}
            checked={picked === oi} onChange={() => onPick(oi)} />
          {opt}
        </label>
      ))}
    </div>
  );
}


function PracticalPart({ attempt, onDone }) {
  const doneScore = attempt.part_scores?.crm;
  const [checked, setChecked] = useState({
    client_created: false, size_clarified: false, bundle_sent: false,
    stage_moved: false, next_task_created: false,
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/academy/final-exam/${attempt.id}/submit-practical`,
        { payload: checked }, { withCredentials: true });
      await onDone();
    } catch (e) {
      alert(e.response?.data?.detail || "Ошибка");
    } finally { setSubmitting(false); }
  };

  if (doneScore !== null && doneScore !== undefined) {
    return (
      <div style={{ background: "white", border: "1px solid #d1fae5",
                     borderRadius: 10, padding: 24, textAlign: "center" }}>
        <Trophy size={32} color={doneScore >= 80 ? "#22c55e" : "#f97316"}
          style={{ margin: "0 auto 8px" }} />
        <div style={{ fontSize: 32, fontWeight: 800,
                       color: doneScore >= 80 ? "#22c55e" : "#f97316" }}>
          {doneScore}%
        </div>
        <div style={{ fontSize: 13, color: "#64748b" }}>Часть CRM сдана</div>
      </div>
    );
  }

  const items = [
    ["client_created", "Создал(а) карточку клиента в песочнице CRM"],
    ["size_clarified", "Уточнил(а) размер и параметры (сохранил в карточке)"],
    ["bundle_sent",    "Отправил(а) подборку из 3-5 моделей через Share Builder"],
    ["stage_moved",    "Передвинул(а) клиента по CRM-этапам (Новый → Диалог → Подборка)"],
    ["next_task_created", "Создал(а) следующую задачу с датой напоминания"],
  ];
  return (
    <div style={{ background: "white", border: "1px solid #efeee9",
                   borderRadius: 10, padding: 22 }}>
      <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22 }}>
        Практическое задание — CRM
      </div>
      <div style={{ fontSize: 13, color: "#64748b", marginTop: 6, marginBottom: 14 }}>
        Откройте <a href="/admin/crm" target="_blank" rel="noreferrer"
                    style={{ color: "#C5A059" }}>песочницу CRM</a> в
        другой вкладке, пройдите весь путь клиента и отметьте выполненные пункты
        ниже. Оценка = процент выполненных шагов.
      </div>
      {items.map(([k, label]) => (
        <label key={k} data-testid={`final-practical-${k}`}
          style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0",
                    cursor: "pointer", fontSize: 13 }}>
          <input type="checkbox" checked={checked[k]}
            onChange={e => setChecked({ ...checked, [k]: e.target.checked })} />
          {label}
        </label>
      ))}
      <button data-testid="final-practical-submit"
        onClick={submit} disabled={submitting}
        style={{ background: "#0A0A0A", color: "#D4B878", border: "none",
                  borderRadius: 8, padding: "12px 24px", cursor: "pointer",
                  fontWeight: 700, fontSize: 13, marginTop: 14 }}>
        {submitting ? "Отправка…" : "Сдать практику"}
      </button>
    </div>
  );
}


function ObjectionsPart({ attempt, onDone }) {
  const scenarios = attempt.objection_scenarios || [];
  const done = attempt.objection_done || [];
  const [activeSim, setActiveSim] = useState(null);
  const doneScore = attempt.part_scores?.objections;

  if (doneScore !== null && doneScore !== undefined) {
    return (
      <div style={{ background: "white", border: "1px solid #d1fae5",
                     borderRadius: 10, padding: 24, textAlign: "center" }}>
        <Trophy size={32} color={doneScore >= 80 ? "#22c55e" : "#f97316"}
          style={{ margin: "0 auto 8px" }} />
        <div style={{ fontSize: 32, fontWeight: 800,
                       color: doneScore >= 80 ? "#22c55e" : "#f97316" }}>
          {doneScore}%
        </div>
        <div style={{ fontSize: 13, color: "#64748b" }}>
          3 из 3 возражений отработаны
        </div>
      </div>
    );
  }

  if (activeSim) {
    return <SimWrapper attempt={attempt} scenarioId={activeSim} part={4}
      onFinish={async () => { setActiveSim(null); await onDone(); }} />;
  }

  return (
    <div style={{ background: "white", border: "1px solid #efeee9",
                   borderRadius: 10, padding: 22 }}>
      <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22 }}>
        Возражения — 3 AI-клиента
      </div>
      <div style={{ fontSize: 13, color: "#64748b", marginTop: 6, marginBottom: 14 }}>
        Отработайте каждое возражение. Оценка = средний балл по 3 сценариям.
      </div>
      {scenarios.map(sid => (
        <div key={sid}
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "12px 0", borderTop: "1px solid #f5f4ef" }}>
          <div style={{ fontSize: 13 }}>{sid}</div>
          <button data-testid={`final-sim-start-${sid}`}
            onClick={() => setActiveSim(sid)}
            style={{ background: done.includes(sid) ? "#f5f4ef" : "#0A0A0A",
                      color: done.includes(sid) ? "#64748b" : "#D4B878",
                      border: "none", borderRadius: 6, padding: "8px 16px",
                      cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
            {done.includes(sid) ? "Пройти ещё раз" : "Начать"}
          </button>
        </div>
      ))}
    </div>
  );
}


function FullSalePart({ attempt, onDone }) {
  const doneScore = attempt.part_scores?.full_sale;
  const [active, setActive] = useState(false);
  const scenarioId = attempt.full_sale_scenario || "final_full_sale";

  if (doneScore !== null && doneScore !== undefined) {
    return (
      <div style={{ background: "white", border: "1px solid #d1fae5",
                     borderRadius: 10, padding: 24, textAlign: "center" }}>
        <Trophy size={32} color={doneScore >= 80 ? "#22c55e" : "#f97316"}
          style={{ margin: "0 auto 8px" }} />
        <div style={{ fontSize: 32, fontWeight: 800,
                       color: doneScore >= 80 ? "#22c55e" : "#f97316" }}>
          {doneScore}%
        </div>
        <div style={{ fontSize: 13, color: "#64748b" }}>Полная продажа завершена</div>
      </div>
    );
  }

  if (active) {
    return <SimWrapper attempt={attempt} scenarioId={scenarioId} part={5}
      onFinish={async () => { setActive(false); await onDone(); }} />;
  }
  return (
    <div style={{ background: "white", border: "1px solid #efeee9",
                   borderRadius: 10, padding: 22 }}>
      <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22 }}>
        Полная продажа с нуля
      </div>
      <div style={{ fontSize: 13, color: "#64748b", marginTop: 6, marginBottom: 14 }}>
        Проведите виртуального клиента через: приветствие → выявление
        потребности → подборка → возражение → следующий шаг → CRM.
      </div>
      <button data-testid="final-fullsale-start"
        onClick={() => setActive(true)}
        style={{ background: "#0A0A0A", color: "#D4B878", border: "none",
                  borderRadius: 8, padding: "12px 24px", cursor: "pointer",
                  fontWeight: 700, fontSize: 13 }}>
        <Sparkles size={13} style={{ verticalAlign: "middle", marginRight: 6 }} />
        Начать симуляцию
      </button>
    </div>
  );
}


/**
 * Wrapper around <SimulatorChat>: waits for the AI verdict, then
 * uploads the session_id to the final-exam attempt so the aggregate
 * scorer can pick it up.
 */
function SimWrapper({ attempt, scenarioId, part, onFinish }) {
  const handleFinished = async (sid) => {
    try {
      await axios.post(`${API}/academy/final-exam/${attempt.id}/submit-simulator`,
        { part, scenario_id: scenarioId, session_id: sid },
        { withCredentials: true });
    } catch (e) {
      alert(e.response?.data?.detail || "Не удалось засчитать симуляцию");
    }
    onFinish();
  };
  return (
    <SimulatorChat scenarioId={scenarioId} onFinished={handleFinished} />
  );
}


function FinalResult({ result, scores, navigate }) {
  const passed = result.passed;
  return (
    <div style={{ background: "linear-gradient(135deg,#0A0A0A,#1a1a1a)",
                   border: "1px solid #C5A059", borderRadius: 14, padding: 40,
                   textAlign: "center", color: "#e5e7eb" }}>
      <Trophy size={48} color="#D4B878" style={{ margin: "0 auto 12px" }} />
      <div style={{ fontFamily: "Cormorant Garamond", fontSize: 32, color: "#D4B878" }}>
        {passed ? "🏆 Обучение завершено!" : "Не пройдено"}
      </div>
      <div style={{ fontSize: 44, fontWeight: 800, margin: "18px 0",
                     color: passed ? "#22c55e" : "#f97316" }}>
        {result.aggregate_score}%
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))",
                     gap: 10, maxWidth: 500, margin: "20px auto",
                     fontSize: 13, color: "#a1a1aa" }}>
        <div>Продукт: <b style={{ color: "#e5e7eb" }}>{scores.product}%</b></div>
        <div>Продажи: <b style={{ color: "#e5e7eb" }}>{scores.sales}%</b></div>
        <div>CRM: <b style={{ color: "#e5e7eb" }}>{scores.crm}%</b></div>
        <div>Возражения: <b style={{ color: "#e5e7eb" }}>{scores.objections}%</b></div>
        <div>Практика: <b style={{ color: "#e5e7eb" }}>{scores.full_sale}%</b></div>
      </div>
      <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 10 }}>
        Проходной балл — {result.pass_threshold}%
      </div>
      <button data-testid="final-back-to-academy"
        onClick={() => navigate("/academy")}
        style={{ background: "linear-gradient(135deg,#C5A059,#D4B878)",
                  color: "#0A0A0A", border: "none", borderRadius: 8,
                  padding: "14px 28px", fontWeight: 800, fontSize: 13,
                  textTransform: "uppercase", cursor: "pointer",
                  marginTop: 20 }}>
        {passed ? "Отправить заявку на перевод" : "Вернуться в академию"}
      </button>
    </div>
  );
}
